import { useEffect, useState } from "react";
import {
  ApiError,
  fetchExecutionStackBook,
  fetchExecutionStackRisk,
  fetchEquityCurve,
  postRearm,
  type ExecutionStackBookDto,
  type RiskStateDto,
  type EquityCurveDto,
} from "../api";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { EquityCurveChart } from "../components/EquityCurveChart";

const SOFT_DRAWDOWN_LEVEL = -0.08;
const HARD_DRAWDOWN_FLOOR = -0.20;

function pctFmt(v: number | null, digits = 2): string {
  if (v === null) return "—";
  return `${(v * 100).toFixed(digits)}%`;
}

function weightFmt(v: number): string {
  return `${(v * 100).toFixed(2)}%`;
}

function DecisionChip({ decision }: { decision: string }) {
  const cls =
    decision === "admit" ? "xs-chip xs-chip-ok" :
    decision === "trim" ? "xs-chip xs-chip-warn" :
    decision === "drop" ? "xs-chip xs-chip-bad" : "xs-chip xs-chip-mute";
  return <span className={cls}>{decision}</span>;
}

/** The taper gauge: soft level / floor / current drawdown marker,
 * geometry-exact against the SAME two constants app/factory/risk.py
 * uses (restated here only for the gauge's visual scale, never for any
 * computation -- the multiplier and state shown always come straight
 * from the API response). */
function TaperGauge({ drawdown, multiplier }: { drawdown: number | null; multiplier: number | null }) {
  const domainMin = -0.25; // a hair past the floor, so the marker never sits on the edge
  const clampPct = (v: number) => Math.max(0, Math.min(100, ((v - domainMin) / (0 - domainMin)) * 100));
  const softPct = clampPct(SOFT_DRAWDOWN_LEVEL);
  const floorPct = clampPct(HARD_DRAWDOWN_FLOOR);
  const ddPct = drawdown !== null ? clampPct(drawdown) : null;

  return (
    <div className="xs-gauge-wrap">
      <div className="xs-gauge-track">
        <div className="xs-gauge-zone xs-gauge-zone-full" style={{ left: `${softPct}%`, right: 0 }} />
        <div className="xs-gauge-zone xs-gauge-zone-taper" style={{ left: `${floorPct}%`, width: `${softPct - floorPct}%` }} />
        <div className="xs-gauge-zone xs-gauge-zone-tripped" style={{ left: 0, width: `${floorPct}%` }} />
        <div className="xs-gauge-mark" style={{ left: `${softPct}%` }} title={`soft level ${pctFmt(SOFT_DRAWDOWN_LEVEL)}`} />
        <div className="xs-gauge-mark" style={{ left: `${floorPct}%` }} title={`hard floor ${pctFmt(HARD_DRAWDOWN_FLOOR)}`} />
        {ddPct !== null && (
          <div className="xs-gauge-current" style={{ left: `${ddPct}%` }} title={`current drawdown ${pctFmt(drawdown)}`} />
        )}
      </div>
      <div className="xs-gauge-labels">
        <span>{pctFmt(domainMin, 0)}</span>
        <span>floor {pctFmt(HARD_DRAWDOWN_FLOOR, 0)}</span>
        <span>soft {pctFmt(SOFT_DRAWDOWN_LEVEL, 0)}</span>
        <span>0%</span>
      </div>
      <div className="xs-gauge-readout">
        drawdown <b>{pctFmt(drawdown)}</b> &middot; multiplier <b>{multiplier !== null ? multiplier.toFixed(3) : "—"}</b>
      </div>
    </div>
  );
}

function RiskPanel({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [risk, setRisk] = useState<RiskStateDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRearm, setShowRearm] = useState(false);
  const [rearmNote, setRearmNote] = useState("");
  const [rearmBusy, setRearmBusy] = useState(false);
  const [rearmError, setRearmError] = useState<string | null>(null);

  function load() {
    fetchExecutionStackRisk(token)
      .then(setRisk)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) return onUnauthorized();
        setError(err instanceof ApiError ? err.message : "request failed");
      });
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function confirmRearm() {
    setRearmBusy(true);
    setRearmError(null);
    try {
      const fresh = await postRearm(token, rearmNote);
      setRisk(fresh); // show the FRESHLY re-evaluated state -- never an optimistic guess
      setShowRearm(false);
      setRearmNote("");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return onUnauthorized();
      setRearmError(err instanceof ApiError ? err.message : "re-arm failed");
    } finally {
      setRearmBusy(false);
    }
  }

  if (error) return <div className="card xs-card"><h2>Risk</h2><div className="xs-error">{error}</div></div>;
  if (!risk) return <div className="card xs-card"><h2>Risk</h2><div className="page-sub">Loading…</div></div>;

  return (
    <div className="card xs-card">
      <h2>Risk <FieldHelp term="kill_switch" /></h2>
      <div className="xs-honest-banner">
        Analytical / simulation only -- this never places or affects a real order.
      </div>

      {risk.status === "not_started" ? (
        <div className="xs-empty-state">
          <b>Not started yet.</b> {risk.reason}
          <div className="page-sub" style={{ marginTop: 6 }}>
            Once the daily snapshot job runs for the first time, this panel will show real
            drawdown from that day forward -- never backfilled.
          </div>
        </div>
      ) : (
        <>
          <div className="xs-risk-top">
            <span className={`xs-state-badge ${risk.state === "tripped" ? "xs-state-tripped" : "xs-state-armed"}`}>
              {risk.state?.toUpperCase()}
            </span>
            <span className="page-sub">
              {risk.days_tracked} day{risk.days_tracked === 1 ? "" : "s"} tracked so far
              <FieldHelp term="book_equity_clock" />
              {risk.days_tracked < 20 && (
                <> -- a short history; treat the drawdown number as an early reading, not a validated result.</>
              )}
            </span>
          </div>

          <TaperGauge drawdown={risk.drawdown_pct} multiplier={risk.exposure_multiplier} />
          <FieldHelp term="drawdown_taper" />

          <div className="xs-reason">{risk.reason}</div>

          <dl className="xs-dl">
            <dt>Peak equity</dt><dd>{risk.peak_equity?.toFixed(4) ?? "—"}</dd>
            <dt>Current equity</dt><dd>{risk.current_equity?.toFixed(4) ?? "—"}</dd>
            <dt>Would-restore multiplier</dt><dd>{risk.would_restore_multiplier?.toFixed(3) ?? "—"}</dd>
          </dl>

          {risk.trip_log.length > 0 && (
            <>
              <h3 className="xs-subhead">Trip log</h3>
              <table className="xs-table">
                <thead><tr><th>Date</th><th>Drawdown at trip</th><th>Equity at trip</th><th>Peak at trip</th></tr></thead>
                <tbody>
                  {risk.trip_log.map((t) => (
                    <tr key={t.as_of}>
                      <td>{t.as_of}</td>
                      <td>{pctFmt(t.drawdown_at_trip)}</td>
                      <td>{t.equity_at_trip.toFixed(4)}</td>
                      <td>{t.peak_equity_at_trip.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {risk.state === "tripped" && (
            <div className="xs-rearm-box">
              <button className="xs-btn xs-btn-warn" onClick={() => setShowRearm(true)}>
                Manual re-arm&hellip;
              </button>
            </div>
          )}
        </>
      )}

      {showRearm && risk && (
        <div className="xs-modal-backdrop" onClick={() => !rearmBusy && setShowRearm(false)}>
          <div className="xs-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm manual re-arm</h3>
            <p className="page-sub">
              Current drawdown is <b>{pctFmt(risk.drawdown_pct)}</b>. Re-arming now would resume at{" "}
              <b>{risk.would_restore_multiplier?.toFixed(3)}&times;</b> exposure
              {risk.would_restore_multiplier !== null && risk.would_restore_multiplier < 1 ? (
                <> -- <b>not full</b>, drawdown is still inside the taper band.</>
              ) : risk.would_restore_multiplier !== null && risk.would_restore_multiplier >= 0.999 ? (
                <> -- full exposure, drawdown has recovered above the soft level.</>
              ) : (
                <> -- if drawdown is still at/beyond the floor, this will immediately re-trip.</>
              )}
            </p>
            <label className="xs-label">
              Note (optional)
              <input
                className="xs-input"
                value={rearmNote}
                onChange={(e) => setRearmNote(e.target.value)}
                placeholder="why are you re-arming now?"
              />
            </label>
            {rearmError && <div className="xs-error">{rearmError}</div>}
            <div className="xs-modal-actions">
              <button className="xs-btn" onClick={() => setShowRearm(false)} disabled={rearmBusy}>Cancel</button>
              <button className="xs-btn xs-btn-warn" onClick={confirmRearm} disabled={rearmBusy}>
                {rearmBusy ? "Re-arming…" : "Confirm re-arm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EquityCurvePanel({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [curve, setCurve] = useState<EquityCurveDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchEquityCurve(token)
      .then(setCurve)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) return onUnauthorized();
        setError(err instanceof ApiError ? err.message : "request failed");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (error) return <div className="card xs-card"><h2>Forward equity record</h2><div className="xs-error">{error}</div></div>;
  if (!curve) return <div className="card xs-card"><h2>Forward equity record</h2><div className="page-sub">Loading…</div></div>;

  return (
    <div className="card xs-card">
      <h2>Forward equity record <FieldHelp term="equity_curve_benchmark" /></h2>
      {curve.status === "not_started" ? (
        <div className="xs-empty-state">{curve.reason}</div>
      ) : (
        <>
          <EquityCurveChart series={curve.series} />
          {curve.early_reading_caveat && (
            <div className="xs-empty-state" style={{ marginTop: 8 }}>
              Early reading: only {curve.days_tracked} day(s) tracked (floor for a settled reading is{" "}
              {curve.early_reading_floor_days}).
            </div>
          )}
          {curve.cumulative_return && (
            <div className="xs-dl" style={{ marginTop: 10 }}>
              <span className="page-sub">
                Cumulative: book {pctFmt(curve.cumulative_return.book)}
                {curve.cumulative_return.benchmark !== null && <> &middot; NIFTY {pctFmt(curve.cumulative_return.benchmark)}</>}
                {curve.cumulative_return.equal_weight !== null && <> &middot; equal-weight {pctFmt(curve.cumulative_return.equal_weight)}</>}
                {curve.vs_benchmark_pct !== null && <> &middot; vs. NIFTY {pctFmt(curve.vs_benchmark_pct)}</>}
              </span>
            </div>
          )}
          <div className="xs-dl" style={{ marginTop: 4 }}>
            <span className="page-sub">
              Turnover <FieldHelp term="equal_weight_comparator" />
              {": "}
              {curve.latest_turnover ? curve.latest_turnover.note : "no reconstruction with a recoverable prior state yet."}
            </span>
          </div>
          <div className="xs-honest-banner" style={{ marginTop: 8 }}>{curve.honesty_note}</div>
        </>
      )}
    </div>
  );
}

function SizingPanel({ book }: { book: ExecutionStackBookDto }) {
  const total = Object.values(book.excluded_summary).reduce((a, b) => a + b, 0);
  const allAtCap = book.held.length > 0 && book.held.every((h) => Math.abs(h.sizing_final_size - book.per_name_cap) < 1e-6);
  return (
    <div className="card xs-card">
      <h2>Sizing</h2>
      <p className="page-sub">
        Per-stock Kelly-sized edge, as of the last reconstruction ({book.reconstructed_as_of},{" "}
        {book.days_since_reconstruction} day{book.days_since_reconstruction === 1 ? "" : "s"} ago).
      </p>
      {allAtCap && (
        <div className="xs-honest-banner">
          All selected names clip to the {weightFmt(book.per_name_cap)} per-name cap; raw Kelly sizes are far
          larger (edge divided by short-horizon variance runs very high), so the cap -- not the Kelly
          fraction itself -- is the binding risk control here. Sizing differentiates the selection{" "}
          <b>ranking</b>, not these final weights -- the identical 5.00%s below are the expected,
          correct result, not a bug.
        </div>
      )}
      <table className="xs-table">
        <thead>
          <tr>
            <th>Ticker</th><th>Sector</th><th>Sizing (pre-trim)</th>
            <th>Signal age <FieldHelp term="signal_half_life" /></th>
            <th>Est. edge remaining</th>
          </tr>
        </thead>
        <tbody>
          {book.held.map((h) => (
            <tr key={h.security_id}>
              <td title={h.security_id}>{h.display_ticker || h.security_id}</td>
              <td>{h.sector_key ?? "—"}</td>
              <td className="xs-num">{weightFmt(h.sizing_final_size)}</td>
              <td className="xs-num" title={h.signal_run_start_date ? `continuous BUY since ${h.signal_run_start_date}` : "no qualifying BUY run on record"}>
                {h.signal_age_trading_days === null ? "—" : `${h.signal_age_trading_days}d`}
              </td>
              <td className="xs-num" title={h.edge_remaining_note}>
                {h.est_edge_remaining_pct === null ? (
                  <span className="page-sub" style={{ fontSize: ".72rem" }}>not yet measurable</span>
                ) : (
                  `${h.est_edge_remaining_pct.toFixed(2)}%`
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <h3 className="xs-subhead">Honest exclusions ({total} of {book.universe_scanned ?? "?"} scanned)</h3>
      <div className="xs-exclusion-grid">
        {Object.entries(book.excluded_summary).map(([bucket, count]) => (
          <div key={bucket} className="xs-exclusion-cell">
            <span className="xs-exclusion-count">{count}</span>
            <span className="xs-exclusion-label">{bucket.replace(/_/g, " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PortfolioPanel({ book }: { book: ExecutionStackBookDto }) {
  return (
    <div className="card xs-card">
      <h2>Portfolio <FieldHelp term="effective_breadth" /></h2>
      <dl className="xs-dl">
        <dt>Effective breadth</dt>
        <dd>{book.effective_breadth !== null ? book.effective_breadth.toFixed(2) : "—"} independent bets ({book.held.length} names held)</dd>
        <dt>Deployed</dt><dd>{weightFmt(book.deployed_pct)}</dd>
        <dt>Cash buffer</dt><dd>{weightFmt(book.cash_buffer_pct)}</dd>
      </dl>

      <h3 className="xs-subhead">Sector exposure vs. {weightFmt(book.sector_cap)} cap</h3>
      <div className="xs-sector-bars">
        {Object.entries(book.sector_exposure).sort((a, b) => b[1] - a[1]).map(([sector, w]) => (
          <div key={sector} className="xs-sector-row">
            <span className="xs-sector-name">{sector}</span>
            <div className="xs-sector-track">
              <div
                className={`xs-sector-fill ${w >= book.sector_cap - 1e-9 ? "xs-sector-fill-bound" : ""}`}
                style={{ width: `${Math.min(100, (w / book.sector_cap) * 100)}%` }}
              />
            </div>
            <span className="xs-num">{weightFmt(w)}</span>
          </div>
        ))}
      </div>

      <h3 className="xs-subhead">Decision trail &amp; Day-1 equal-weight comparison</h3>
      <table className="xs-table">
        <thead>
          <tr>
            <th>#</th><th>Ticker</th><th>Decision</th><th>Reason</th>
            <th>Constructed weight</th><th>Equal-weight (1/N)</th>
          </tr>
        </thead>
        <tbody>
          {book.decisions.map((d) => (
            <tr key={d.security_id}>
              <td>{d.rank}</td>
              <td title={d.security_id}>{d.display_ticker || d.security_id}</td>
              <td><DecisionChip decision={d.decision} /></td>
              <td className="xs-reason-cell">{d.reason}</td>
              <td className="xs-num">{weightFmt(d.final_weight)}</td>
              <td className="xs-num">{weightFmt(book.equal_weight_comparison[d.security_id] ?? 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ExecutionStackPage({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [book, setBook] = useState<ExecutionStackBookDto | null>(null);
  const [bookError, setBookError] = useState<string | null>(null);

  useEffect(() => {
    fetchExecutionStackBook(token)
      .then(setBook)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) return onUnauthorized();
        setBookError(err instanceof ApiError ? err.message : "request failed");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="shell" style={{ maxWidth: 1040 }}>
      <div className="page-head">
        <h1>Execution Stack</h1>
        <div className="page-sub">Sizing &rarr; Portfolio &rarr; Risk, end to end.</div>
      </div>
      <PageHelper>
        This is the execution-stack chain: Sizing computes a Kelly-sized position per stock,
        Portfolio admits/trims/drops them for correlation and sector concentration, and Risk
        governs the whole book's exposure by realized drawdown. Everything here is analytical /
        simulation -- it never places a real order. The Sizing and Portfolio panels show a
        weekly-refreshed cache (recomputing live takes ~2.5 minutes); the Risk panel is live.
      </PageHelper>

      {bookError && <div className="card xs-card"><div className="xs-error">{bookError}</div></div>}
      {!book && !bookError && <div className="card xs-card"><div className="page-sub">Loading…</div></div>}
      {book && <SizingPanel book={book} />}
      {book && <PortfolioPanel book={book} />}
      <RiskPanel token={token} onUnauthorized={onUnauthorized} />
      <EquityCurvePanel token={token} onUnauthorized={onUnauthorized} />
    </div>
  );
}
